/*eslint max-lines: ["error", 500]*/
import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm, NgModel, AbstractControl } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { TmsApiService } from '../../shared/services/tms-api/tms-api.service';
import { ConfigService } from '../../shared/services/config/config.service';
import { EntitlementModel } from '../../shared/models/entitlement.model';
import { TranslationsService } from '../../shared/services/translations/translations.service';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { Constants } from '../../shared/constants';
import { sprintf } from 'sprintf-js';
import { take } from 'rxjs/operators';
import { AnalyticsHandler } from '../../shared/libraries/analytics-handler/analytics-handler';
import { ProfileService } from '../../shared/services/profile/profile.service';

@Component({
  selector: 'app-ticket-link',
  templateUrl: './ticket-link.component.html',
  styleUrls: ['./ticket-link.component.scss']
})
export class TicketLinkComponent implements OnInit, OnDestroy {
  /**
   * Visual IDs Form
   * @var {NgForm}
   */
  @ViewChild('visualIdsForm') visualIdsForm: NgForm;
  @ViewChildren('vidInputsRef') _vidInputElements: ElementRef;

  /**
   * Entitlements model to handle form controls
   * @var Array<any>
   */
  public entitlements: Array<any> = [];

  /**
   * Array of Entitlements already validated from TMS
   * @var {Array<EntitlementModel>}
   */
  public validatedEntitlements: Array<EntitlementModel> = [];

  /**
   * Carousel slides
   * @var {Array<any>}
   */
  public carouselSlides: Array<any>;

  /**
   * Counter to manage Visual ID Forms controls identifiers
   * @var {number}
   */
  public visualIdFieldsAdded = 0;

  /**
   * @var {Array<string>}
   */
  public generalTmsError: Array<string> = [];

  /**
   * Min length for VID input
   * @var {any}
   */
  public vidInputLength: any;

  /**
   * Check if entitlements validation process is in progress
   * @var {boolean}
   */
  public isValidationInProgress = false;

  /**
   * The URL for MEP claims linking page
   * @var {string}
   */
  public mepClaimUrl = '';

  /**
   * Entitlements subscriber
   * @var {Subscription}
   */
  private _entitlementsSubscription: Subscription;

  /**
   * Application configuration object
   * @var {any}
   */
  private _config: any;

  /**
   * Translations object
   * @var {any}
   */
  private _translations: any;

  /**
   * TMS errors subscription
   * @var {Subscription}
   */
  private _generalTmsErrorSubscription: Subscription;

  /**
   * Toggle for enabling cast reservations
   * @var {boolean}
   */
  private _castReservationsEnabled = false;

  /**
   * Component constructor
   *
   * @param _tmsApiService
   * @param _configService
   * @param _translationsService
   * @param _router
   * @param _activeRoute
   * @param _translatePipe
   */
  constructor(
    private _profileService: ProfileService,
    private _tmsApiService: TmsApiService,
    private _configService: ConfigService,
    private _translationsService: TranslationsService,
    private _router: Router,
    private _activeRoute: ActivatedRoute,
    private _translatePipe: TranslatePipe,
    private _analytics: AnalyticsHandler
  ) {
    this._config = this._configService.getConfig();
    this._translations = this._translationsService.getTranslations();
    this.vidInputLength = this._config.vidInputLength;

    this._generateNewVisualIdControl();
  }

  /**
   * On init method
   *
   * @returns {void}
   */
  public ngOnInit(): void {
    this.carouselSlides = this._generateCarouselData();

    this._entitlementsSubscription = this._tmsApiService.validEntitlementsSubject.subscribe(
      (entitlements: EntitlementModel[]) => {
        this.validatedEntitlements = entitlements;
      }
    );

    this._generalTmsErrorSubscription = this._tmsApiService.generalTmsErrorSubject.subscribe(
      (message: string) => {
        if (!!message) {
          this.generalTmsError = [this._translatePipe.transform(message)];
        }
      }
    );

    this._analytics.getModel().then((model: any) => {
        model.pageId = 'landing';
        this._analytics.update();
    });

    this._castReservationsEnabled = this._config.castReservationsEnabled;
    this.mepClaimUrl = this._config.mepClaimUrl;
  }

  /**
   * Execute the min length validation of the inputs programatically, providing a better user exepriencie
   * instead of using blur/keypress/submit events.
   * @param vidInput
   * @param visualId
   * @param vidIndex
   * @returns {boolean}
   */
  private _validateInputMinLength(vidInput: AbstractControl, visualId: string, vidIndex: number): boolean {
    let hasValidLenght = true;

    if (visualId.length < this.vidInputLength.min) {
      hasValidLenght = false;
      vidInput.setErrors({'minlength': true});
    }

    return hasValidLenght;
  }

  /**
   * Validate VID inputs values against TMS once the user click continue
   * @returns {void}
   */
  public validateEntitlements() {
    this.isValidationInProgress = true;
    this._clearGeneralErrors();

    const observablesArray = [];
    const errorObject = {};
    let areEntitlementsValid = true;
    const formControls = Object.values(this.visualIdsForm.form.controls);
    let validateSubscription: Subscription;

    for (let i = 0; i < this.entitlements.length; i++) {
      const visualId = this._formatVisualId(this.entitlements[i]['entitlementId']);

      if (!this._validateInputMinLength(formControls[i], visualId, i)) {
        areEntitlementsValid = false;
      }

      if (areEntitlementsValid) {
        const validateLambda = this._tmsApiService.validateVisualIdWithParameter(visualId);
        observablesArray.push(validateLambda);
      }
    }

    if (!areEntitlementsValid) {
      this.isValidationInProgress = false;

      return;
    }

    validateSubscription = forkJoin(observablesArray).subscribe(
      (res) => {
        const successfulResponse = [
          Constants.TICKET_LINKED,
          Constants.TICKET_UNLINKED
        ];

        for (let i = 0; i < res.length; i++) {
          if (successfulResponse.indexOf(res[i]['status']) !== -1) {
            // Add validated visual id to the service which will be used to render /assign page
            this._tmsApiService.addEntitlement(res[i], i);
            this.entitlements[i]['validated'] = true;

            // Register the completition of the validation process
            this.isValidationInProgress = false;
          } else {
            // Remove previous VID validated and map errors to the specific inputs
            this._tmsApiService.removePreviousValidVid(i);
            this.entitlements[i]['validated'] = false;

            // Handle errors display process on the related VID input
            errorObject[res[i]['status']] = true;
            formControls[i].setErrors(errorObject);
            areEntitlementsValid = false;

            // Register the completition of the validation process
            this.isValidationInProgress = false;
          }
        }
      },
      (error) => {
        // Handle errors display process on the related VID input
        this.generalTmsError = [
          this._translatePipe.transform(Constants.GENERIC_ERROR)
        ];
        areEntitlementsValid = false;

        // Register the completition of the validation process
        this.isValidationInProgress = false;

      },
      () => {
        validateSubscription.unsubscribe();

        if (areEntitlementsValid) {
          this.assignTickets();
        }
      }
    );
  }

  /**
   * Adds a new visual ID FormGroup to the Form
   *
   * @returns {void}
   */
  public addVisualId(): void {
    if (this.canAddMoreVisualIds()) {
      this._generateNewVisualIdControl();
    }
  }

  /**
   * Retrieve Visual ID Input error key to display the error
   *
   * @param errors
   *
   * @returns {string}
   */
  public getVisualIdInputErrorKey(errors: any): string {
    return `link-page.input-error-${Object.keys(errors)[0]}`;
  }

  /**
   * Checks if guest can add more visual IDs to the current form
   *
   * @returns {boolean}
   */
  public canAddMoreVisualIds(): boolean {
    return this.entitlements.length < Number(this._config.maxEntitlementsAllowed);
  }

  /**
   * Check if we should show icon container after a specific VID input
   *
   * @param visualIdInput
   *
   * @returns {boolean}
   */
  public shouldShowIconContainer(visualIdInput: NgModel): boolean {
    return this.entitlements.length > 1;
  }

  /**
   * Checks if guest can continue to assign tickets page
   *
   * @returns {boolean}
   */
  public canAssignTickets(): boolean {
    return !!this.validatedEntitlements.length;
  }

  /**
   * Continues to tickets assign page
   *
   * @returns {void}
   */
  public assignTickets(): void {
    this._tmsApiService.clearTmsApiGeneralError();

    this._router.navigate(
      ['/assign'],
      {
        relativeTo: this._activeRoute
      }
    );
  }

  /**
   * Remove visual id input and remove it from service object if it was a valid one
   *
   * @param visualIdIndex
   *
   * @returns {void}
   */
  public removeVisualIdInput(visualIdIndex: number): void {
    this._tmsApiService.removePreviousValidVid(visualIdIndex);
    this.entitlements.splice(visualIdIndex, 1);
  }

  /**
   * On destroy method
   *
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.validatedEntitlements = [];
    this._entitlementsSubscription.unsubscribe();
    this._generalTmsErrorSubscription.unsubscribe();
  }

  /**
   * @TODO Review
   * Handle logic when user click Cancel
   *
   * @returns {void}
   */
  public onCancel(): void {
    const referrerUrl = localStorage.getItem('claimReferrer');

    window.location.href = !!referrerUrl ? referrerUrl : window.location.origin;
  }

  /**
   * Determine if user has MEP and claim link can be displayed
   *
   * @returns {boolean}
   */
  public displayClaimLink(): boolean {
    const { affiliations } = this._profileService.getProfile();
    const hasMep = affiliations.includes(Constants.MEP_AFFILIATION);

    return !hasMep && this._castReservationsEnabled;
  }

  /**
   * Return formatted visual ID.
   * @param visualId
   * @returns {string}
   */
  private _formatVisualId(visualId: string): string {
    return visualId.replace(/\s/g, '');
  }

  /**
   * Generates the carousel data based on DScribe content
   *
   * @returns {any}
   */
  private _generateCarouselData(): any {
    const carouselData = [];
    let slideSrc;
    let slideAlt;
    let index = 1;

    for (const key in this._translations) {
      if (Constants.CAROUSEL_REGEX.test(key)) {
        slideSrc = `${Constants.RESOURCE_BUNDLE_PREFIX}${sprintf(Constants.CAROUSEL_IMAGE_KEY, index)}`;
        slideAlt = `${Constants.RESOURCE_BUNDLE_PREFIX}${sprintf(Constants.CAROUSEL_ALTTEXT_KEY, index)}`;

        carouselData.push({
          src: this._translations[slideSrc],
          altText: this._translations[slideAlt]
        });

        index++;
      }
    }

    return carouselData;
  }

  /**
   * Generate VisualID form controls with their identifiers
   *
   * @returns {void}
   */
  private _generateNewVisualIdControl(): void {
    this.entitlements.push({
      'id': this.visualIdFieldsAdded,
      'entitlementId': ''
    });

    ++this.visualIdFieldsAdded;

    if (!!this.visualIdsForm) {
      this._vidInputElements['changes'].pipe(
        take(1)
      ).subscribe(elements => {
        elements['last'].nativeElement.focus();
      });
    }
  }

  /**
   * Reset errors displayed at the top of the page.
   */
  private _clearGeneralErrors() {
    this.generalTmsError = [];
  }
}
